import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { instructors, testimonials, bootcampPrograms, contactInfo, logoUrl, featuredInstructor } from '../data/mock';
import { 
  ChevronRight, 
  ChevronLeft,
  Target, 
  Users, 
  Award, 
  Star,
  ArrowRight,
  Quote,
  Send,
  CheckCircle2,
  Mail,
  Phone,
  Calendar,
  Image as ImageIcon,
  Plane,
  Medal,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-slide effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bootcampPrograms.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % bootcampPrograms.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + bootcampPrograms.length) % bootcampPrograms.length);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        source: 'contact_form'
      });
      toast.success('Thank you! We will contact you soon.');
      setFormData({ firstName: '', lastName: '', phone: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookConsultation = () => {
    const subject = encodeURIComponent('Consultation Request - Jay Defence Academy');
    const body = encodeURIComponent('Hello,\n\nI would like to book a consultation regarding your defence preparation programs.\n\nPlease contact me at your earliest convenience.\n\nThank you.');
    window.location.href = `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
  };

  const currentProgram = bootcampPrograms[currentSlide];

  return (
    <Layout>
      {/* Hero Section with Carousel */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-navy-900">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800/90 to-olive-900/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content - Carousel */}
            <div className="space-y-8">
              {/* Program Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/20 rounded-full border border-gold-500/30">
                <span className="w-2 h-2 bg-gold-400 rounded-full animate-pulse" />
                <span className="text-gold-400 text-sm font-medium">Registration: ₹{currentProgram.registration} Only</span>
              </div>
              
              {/* Slide Content */}
              <div className="min-h-[280px] transition-all duration-500">
                <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6">
                  {currentProgram.name.split(' ')[0]}{' '}
                  <span className="text-gold-400">{currentProgram.name.split(' ').slice(1).join(' ')}</span>
                </h1>
                
                <p className="text-xl text-gold-300 font-semibold mb-4">
                  {currentProgram.tagline}
                </p>
                
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  {currentProgram.description}
                </p>
                
                <ul className="space-y-2 mb-6">
                  {currentProgram.highlights.map((highlight, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-gold-400 flex-shrink-0" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={handleBookConsultation}
                  size="lg" 
                  className="bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold text-lg px-8 py-6 transition-all duration-300 hover:shadow-xl hover:shadow-gold-500/30 group"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Book a Consultation Today
                  <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                </Button>
                <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}>
                  <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-6">
                    <Phone className="w-5 h-5 mr-2" />
                    Call: 7204064650
                  </Button>
                </a>
              </div>
              
              {/* Carousel Navigation */}
              <div className="flex items-center gap-4 pt-4">
                <button 
                  onClick={prevSlide}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-2">
                  {bootcampPrograms.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        currentSlide === index 
                          ? 'w-8 bg-gold-400' 
                          : 'w-2 bg-white/30 hover:bg-white/50'
                      }`}
                    />
                  ))}
                </div>
                
                <button 
                  onClick={nextSlide}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                
                <span className="text-gray-400 text-sm ml-2">
                  {currentSlide + 1} / {bootcampPrograms.length}
                </span>
              </div>
            </div>
            
            {/* Right Side - Stats Cards */}
            <div className="grid grid-cols-2 gap-6">
              <Card className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300">
                <CardContent className="p-6">
                  <Calendar className="w-10 h-10 text-gold-400 mb-4" />
                  <h3 className="text-3xl font-bold text-white mb-2">3</h3>
                  <p className="text-gray-400">Bootcamp Programs</p>
                </CardContent>
              </Card>
              <Card className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300">
                <CardContent className="p-6">
                  <Users className="w-10 h-10 text-gold-400 mb-4" />
                  <h3 className="text-3xl font-bold text-white mb-2">6+</h3>
                  <p className="text-gray-400">Expert Instructors</p>
                </CardContent>
              </Card>
              <Card className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300">
                <CardContent className="p-6">
                  <Award className="w-10 h-10 text-gold-400 mb-4" />
                  <h3 className="text-3xl font-bold text-white mb-2">30+</h3>
                  <p className="text-gray-400">Candidates Trained</p>
                </CardContent>
              </Card>
              <Card className="bg-gold-500/20 border-gold-500/30 backdrop-blur-sm">
                <CardContent className="p-6">
                  <Target className="w-10 h-10 text-gold-400 mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">₹1,000</h3>
                  <p className="text-gold-300">Registration Fee Only</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Overview Section */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Our Bootcamp Programs</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Choose from our specialized training programs designed to help you achieve your defence career goals.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {bootcampPrograms.map((program, index) => (
              <Card key={program.id} className="group bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
                <div className={`h-2 ${
                  index === 0 ? 'bg-gold-500' : index === 1 ? 'bg-olive-600' : 'bg-navy-900'
                }`} />
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-navy-900 mb-2">{program.name}</h3>
                  <p className="text-gold-600 font-semibold text-sm mb-4">{program.tagline}</p>
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed">{program.description}</p>
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-2xl font-bold text-navy-900">₹{program.registration}</span>
                    <span className="text-gray-500 text-sm">Registration</span>
                  </div>
                  <Button 
                    onClick={handleBookConsultation}
                    className={`w-full font-semibold ${
                      index === 0 
                        ? 'bg-gold-500 hover:bg-gold-400 text-navy-900' 
                        : 'bg-navy-900 hover:bg-navy-800 text-white'
                    }`}
                  >
                    Book Consultation
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* OLQ Tracker Promotional Banner */}
      <section className="py-16 bg-gradient-to-r from-olive-900 via-olive-800 to-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/20 rounded-full border border-gold-500/30 mb-6">
                <Target className="w-4 h-4 text-gold-400" />
                <span className="text-gold-400 text-sm font-medium">Free OLQ Habit Tracker</span>
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">
                Build Your <span className="text-gold-400">Officer Like Qualities</span>
              </h2>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                Track your daily habits aligned with the 15 Officer Like Qualities assessed in SSB. 
                Earn points, build discipline, and unlock exclusive discounts on our programs!
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Track all 15 OLQs with daily activities',
                  'Earn 2 points for completing all habits daily',
                  'Get 15% discount at 50 points',
                  'Build habits that will help you crack SSB'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-200">
                    <CheckCircle2 className="w-5 h-5 text-gold-400 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <Link to="/signup">
                  <Button size="lg" className="bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold">
                    Start Tracking Free
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold">
                    Already have an account? Login
                  </Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Target, title: 'Mental', count: '3 habits', color: 'bg-blue-500' },
                { icon: Users, title: 'Social', count: '4 habits', color: 'bg-purple-500' },
                { icon: Award, title: 'Dynamic', count: '7 habits', color: 'bg-orange-500' },
                { icon: Star, title: 'Physical', count: '1 habit', color: 'bg-green-500' }
              ].map((category, index) => (
                <Card key={index} className="bg-white/10 border-white/10 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className={`w-10 h-10 rounded-lg ${category.color} flex items-center justify-center mb-3`}>
                      <category.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-white font-bold mb-1">{category.title}</h3>
                    <p className="text-gray-400 text-sm">{category.count}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Instructor Highlight */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/20 rounded-full border border-gold-500/30 mb-4">
              <Star className="w-4 h-4 text-gold-500 fill-gold-500" />
              <span className="text-gold-600 font-semibold text-sm">New Addition to Our Team</span>
            </div>
            <h2 className="text-4xl font-bold text-navy-900 mt-3 mb-4">Meet Our New Personality Development Coach</h2>
          </div>
          
          <Card className="bg-white border-0 shadow-xl overflow-hidden">
            <CardContent className="p-0">
              <div className="grid lg:grid-cols-5 gap-0">
                {/* Image Section */}
                <div className="lg:col-span-2 relative">
                  <div className="aspect-[4/5] lg:aspect-auto lg:h-full relative overflow-hidden bg-navy-900">
                    <img 
                      src={featuredInstructor.image} 
                      alt={featuredInstructor.name}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white lg:hidden">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="px-2 py-1 bg-gold-500 text-navy-900 rounded text-xs font-bold">NEW</span>
                        <Plane className="w-5 h-5 text-gold-400" />
                        <span className="text-gold-400 font-semibold text-sm">Indian Air Force Veteran</span>
                      </div>
                      <h3 className="text-2xl font-bold">{featuredInstructor.name}</h3>
                      <p className="text-gold-300">{featuredInstructor.role}</p>
                    </div>
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="lg:col-span-3 p-8 lg:p-10">
                  {/* Header - Hidden on mobile, shown on desktop */}
                  <div className="hidden lg:block mb-6">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="px-2 py-1 bg-gold-500 text-navy-900 rounded text-xs font-bold">NEW</span>
                      <Plane className="w-5 h-5 text-gold-500" />
                      <span className="text-gold-600 font-semibold text-sm">Indian Air Force Veteran</span>
                    </div>
                    <h3 className="text-3xl font-bold text-navy-900 mb-1">{featuredInstructor.name}</h3>
                    <p className="text-olive-600 font-semibold">{featuredInstructor.role}</p>
                  </div>
                  
                  {/* Short Bio */}
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {featuredInstructor.shortBio}
                  </p>
                  
                  {/* Achievements */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Medal className="w-5 h-5 text-gold-500" />
                      <h4 className="text-navy-900 font-bold">Key Achievements</h4>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {featuredInstructor.achievements.map((achievement, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-olive-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{achievement}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Expertise */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Briefcase className="w-5 h-5 text-gold-500" />
                      <h4 className="text-navy-900 font-bold">Areas of Expertise</h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {featuredInstructor.expertise.map((skill, index) => (
                        <span 
                          key={index} 
                          className="px-3 py-1.5 bg-navy-900/10 text-navy-900 rounded-full text-sm font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Organizations */}
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-2">Trusted by leading organizations:</p>
                    <div className="flex flex-wrap gap-3">
                      {featuredInstructor.organizations.slice(0, 4).map((org, index) => (
                        <span key={index} className="text-sm text-navy-900 font-medium">
                          {org}{index < 3 ? ' •' : ''}
                        </span>
                      ))}
                      <span className="text-sm text-gray-500">& more</span>
                    </div>
                  </div>
                  
                  {/* CTA */}
                  <div className="mt-6">
                    <Button 
                      onClick={handleBookConsultation}
                      className="bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Book a Session
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Why Choose Jay Defence Academy?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We provide comprehensive training designed to transform you into a confident defence officer.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Users, title: 'Expert Trainers', desc: 'Special trainers who are either veterans or specialists in their fields!' },
              { icon: Target, title: 'Physical Fitness', desc: 'All courses include a physical training module to ensure you are in the best shape for SSB.' },
              { icon: Award, title: 'Daily Classes', desc: 'Daily classes for select programs include all required modules for clearing exams.' }
            ].map((feature, index) => (
              <Card key={index} className="group bg-cream-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-navy-900 flex items-center justify-center mb-6 group-hover:bg-gold-500 transition-colors duration-300">
                    <feature.icon className="w-7 h-7 text-gold-400 group-hover:text-navy-900 transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-navy-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Instructors Section */}
      <section className="py-20 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-gold-400 font-semibold text-sm tracking-wider uppercase">Our Expert Panel</span>
            <h2 className="text-4xl font-bold text-white mt-3 mb-4">Learn From Decorated Veterans</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Jay Defence Academy is led by decorated veterans with unmatched expertise in SSB preparation and assessment.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {instructors.map((instructor) => (
              <Card key={instructor.id} className="bg-white/5 border-white/10 hover:bg-white/10 transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="w-16 h-16 rounded-full bg-olive-600 flex items-center justify-center text-white text-xl font-bold mb-4 group-hover:bg-gold-500 group-hover:text-navy-900 transition-colors duration-300">
                    {instructor.name.split(' ').slice(0, 2).map(n => n[0]).join('')}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">{instructor.name}</h3>
                  <p className="text-gold-400 text-sm font-medium mb-3">{instructor.role}</p>
                  <p className="text-gray-400 text-sm leading-relaxed">{instructor.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-olive-600 font-semibold text-sm tracking-wider uppercase">Success Stories</span>
            <h2 className="text-4xl font-bold text-navy-900 mt-3 mb-4">Insights From Our Past Candidates</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <Quote className="w-10 h-10 text-gold-400 mb-4" />
                  <p className="text-gray-700 leading-relaxed mb-6">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-navy-900 flex items-center justify-center text-white text-sm font-bold">
                      {testimonial.author[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900">{testimonial.author}</p>
                      <div className="flex gap-0.5">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-3 h-3 fill-gold-400 text-gold-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-olive-600 font-semibold text-sm tracking-wider uppercase">Our Gallery</span>
            <h2 className="text-4xl font-bold text-navy-900 mt-3 mb-4">Life at Jay Defence Academy</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Take a glimpse into our training sessions, special events, and the vibrant learning environment at our academy.
            </p>
          </div>
          
          {/* Special SSB Event */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-gold-500 flex items-center justify-center">
                <Star className="w-5 h-5 text-navy-900" />
              </div>
              <h3 className="text-2xl font-bold text-navy-900">Special SSB Event</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_training-center-12/artifacts/459jytxb_Screenshot%202025-10-01%20at%2010.20.00%E2%80%AFAM.png" 
                  alt="SSB Event - Expert Session"
                  className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="font-semibold">SSB Training Session</p>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_training-center-12/artifacts/0m066wm4_WhatsApp%20Image%202025-09-22%20at%2020.55.19.jpg" 
                  alt="SSB Event - Seminar Hall"
                  className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="font-semibold">Defence Career Seminar</p>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_training-center-12/artifacts/vbzzfeqx_WhatsApp%20Image%202025-09-28%20at%2009.56.35.jpg" 
                  alt="SSB Event - Interactive Session"
                  className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="font-semibold">Expert-Led SSB Workshop</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Our Classroom */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-navy-900 flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-gold-400" />
              </div>
              <h3 className="text-2xl font-bold text-navy-900">Our Classroom</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_training-center-12/artifacts/y2114cdj_WhatsApp%20Image%202025-08-31%20at%2010.17.38%20%281%29.jpg" 
                  alt="Classroom - Students in Exam"
                  className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="font-semibold">Mock Test Session</p>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_training-center-12/artifacts/vi10j81f_WhatsApp%20Image%202025-08-31%20at%2010.17.37.jpg" 
                  alt="Classroom - Blackboard"
                  className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="font-semibold">Mathematics Class</p>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_training-center-12/artifacts/kb3whjkj_WhatsApp%20Image%202025-08-31%20at%2010.17.37%20%281%29.jpg" 
                  alt="Classroom - Study Session"
                  className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="font-semibold">Study Environment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-gold-400 font-semibold text-sm tracking-wider uppercase">Get In Touch</span>
              <h2 className="text-4xl font-bold text-white mt-3 mb-6">Contact Us</h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                We would love to help you become an officer for the Indian Armed Forces! 
                Fill out the form and our team will get back to you within 24 hours.
              </p>
              
              <div className="space-y-4 mb-8">
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-3 text-gray-300 hover:text-gold-400 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-gold-500/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-gold-400" />
                  </div>
                  <span>{contactInfo.email}</span>
                </a>
                <a 
                  href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 text-gray-300 hover:text-gold-400 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-gold-500/20 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-gold-400" />
                  </div>
                  <span>{contactInfo.phone}</span>
                </a>
              </div>
              
              <div className="space-y-4">
                {[
                  'Personalized guidance for SSB preparation',
                  'Expert mentorship from decorated veterans',
                  'Proven track record of successful candidates'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold-400" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <Card className="bg-white border-0 shadow-2xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-navy-900">First Name</Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        placeholder="John"
                        required
                        className="border-gray-200 focus:border-gold-500 focus:ring-gold-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-navy-900">Last Name</Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        placeholder="Doe"
                        required
                        className="border-gray-200 focus:border-gold-500 focus:ring-gold-500"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-navy-900">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+91 98765 43210"
                      required
                      className="border-gray-200 focus:border-gold-500 focus:ring-gold-500"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-navy-900">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@example.com"
                      required
                      className="border-gray-200 focus:border-gold-500 focus:ring-gold-500"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-navy-900">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Tell us about your defence career aspirations..."
                      rows={4}
                      className="border-gray-200 focus:border-gold-500 focus:ring-gold-500 resize-none"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-navy-900 hover:bg-navy-800 text-white font-semibold py-6 transition-all duration-300"
                  >
                    {isSubmitting ? 'Sending...' : 'Submit'}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gold-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-navy-900 mb-4">Ready to Start Your Defence Career?</h2>
          <p className="text-navy-800 mb-8 max-w-2xl mx-auto">
            Join hundreds of successful candidates who have cracked the SSB with our expert guidance. Registration starts at just ₹1,000!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={handleBookConsultation}
              size="lg" 
              className="bg-navy-900 hover:bg-navy-800 text-white font-bold text-lg px-10 py-6 transition-all duration-300 hover:shadow-xl group"
            >
              <Mail className="w-5 h-5 mr-2" />
              Book a Consultation Today
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}>
              <Button size="lg" variant="outline" className="border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white font-bold text-lg px-10 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call: 7204064650
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
